import {Ref, onUnmounted, ref, watch} from "vue";
import {MedicationCheckupRunReport, ScanProgress, ScanStep} from "@/medication-checkup/interfaces";
import {BEAT_MS, WORK_FLOOR_MS} from "@/medication-checkup/constants";

/**
 * What a scene running work behind a card hands over: how the work is
 * streamed, what a lost stream falls back on, and how its landings are
 * paced.
 */
export interface ScanRunOptions {
    /**
     * Whether the bubble of the scene has landed: the reveal waits on it, so
     * nothing appears above the words that announce it.
     */
    bubbleShown: Ref<boolean>;
    /**
     * The scene's way of arming a timer, so every landing of the run dies
     * with the scene.
     */
    later: (callback: () => void, delay: number) => number;
    /**
     * Run the work, narrating it: the scene opens the stream of its block and
     * hands it the reporter, which folds every message onto the card.
     */
    stream: (report: (message: MedicationCheckupRunReport) => void, signal: AbortSignal) => Promise<void>;
    /**
     * Ask for the same work plainly, when the stream is lost. Both endpoints
     * converge on the same record, so a lost stream costs the narration and
     * nothing else.
     */
    fallback: () => Promise<unknown>;
    /**
     * What the scene does with the answer before it is revealed — preselect
     * the lines it just collected, for one.
     */
    landed?: () => void;
    /**
     * The minimum time the card stays up, counted from the start of each run
     * — the visit's own opening included, for a scene that arms it as it
     * opens.
     */
    floor?: number;
}

/**
 * Work run behind a card, source by source as the stream reports it, then
 * revealed one beat at a time.
 *
 * The collection scans the record, the vigilance analyses the treatments,
 * the intervention has the AI draft its table; all play the same thing, in
 * the one vocabulary every narrated run speaks, folded here. The streamed
 * request first, with the
 * minimum-display timer running alongside it — the reveal waits on both, on
 * the bubble having landed, and on the work being done. A failed stream
 * falls back on a plain submission of the same work, so a lost stream costs
 * the narration and nothing else. A retry starts the whole thing over, its
 * own floor included, and calls off the reveal of the run it replaces.
 */
export const useScanRun = (options: ScanRunOptions) => {
    /**
     * How many times the work has run this visit, so the card can tell a
     * retry from the first attempt and restart its elapsed counter.
     */
    const run = ref(0);

    /**
     * Whether the work answered this visit. The reveal waits on this rather
     * than on what the served state carries, which a resumed session
     * already has.
     */
    const done = ref(false);

    /**
     * Whether the work could not be had, stream and plain submission alike.
     */
    const failed = ref(false);

    /**
     * Whether the minimum display of the card has elapsed.
     */
    const elapsed = ref(false);

    /**
     * How far the answer is revealed: its recap, then its rows, then the
     * action closing the scene.
     */
    const revealStep = ref(0);

    /**
     * What the stream has reported so far, for the card. Progress only: what
     * the scene renders afterwards comes from the served state.
     */
    const progress = ref<ScanProgress>({steps: []});

    /**
     * The stream in flight, if any. An AbortSignal is single-use, so each run
     * gets its own controller — sharing one would make every retry after an
     * abort reject instantly. Leaving the scene closes the stream, and the
     * server finishes the work on its own.
     */
    let inFlight: AbortController | null = null;

    /**
     * The timers of the reveal alone, called off when a retry replaces the
     * run they were revealing.
     */
    let revealTimers: number[] = [];

    onUnmounted(() => inFlight?.abort());

    /**
     * Open a stage of the narration: every message that follows lands on it.
     */
    const openStep = (title: string, total: number | null): void => {
        progress.value.steps.push({title, total, lines: [], failed: null});
    };

    const openedStep = (): ScanStep | null => progress.value.steps.at(-1) ?? null;

    /**
     * Land one line on the stage currently open.
     */
    const land = (line: string): void => {
        openedStep()?.lines.push(line);
    };

    /**
     * Close the stage currently open on what went wrong with it.
     */
    const fail = (message: string): void => {
        const step = openedStep();

        if (step !== null) {
            step.failed = message;
        }
    };

    /**
     * Fold one message of the stream onto the card: a stage opens, and what
     * follows lands on it.
     */
    const report = (message: MedicationCheckupRunReport): void => {
        switch (message.type) {
            case 'step':
                openStep(message.title, message.lines);
                break;
            case 'line':
                land(message.text);
                break;
            case 'failed':
                fail(message.text);
                break;
        }
    };

    /**
     * Reveal the answer, one beat per step. Called once per run: a reveal
     * under way is left to finish.
     */
    const reveal = (): void => {
        if (revealStep.value > 0) {
            return;
        }

        revealStep.value = 1;
        revealTimers = [
            options.later(() => revealStep.value = 2, BEAT_MS),
            options.later(() => revealStep.value = 3, BEAT_MS * 2)
        ];
    };

    /**
     * Run the work, from scratch — a retry is a run like the first one.
     */
    const start = async (): Promise<void> => {
        revealTimers.splice(0).forEach(id => window.clearTimeout(id));

        run.value += 1;
        done.value = false;
        failed.value = false;
        elapsed.value = false;
        revealStep.value = 0;
        progress.value = {steps: []};

        options.later(() => elapsed.value = true, options.floor ?? WORK_FLOOR_MS);

        const abort = new AbortController();

        inFlight = abort;

        try {
            await options.stream(report, abort.signal);

            options.landed?.();
            done.value = true;

            return;
        } catch {
            if (abort.signal.aborted) {
                return;
            }
        }

        try {
            await options.fallback();

            options.landed?.();
            done.value = true;
        } catch {
            failed.value = true;
        }
    };

    watch(
        () => options.bubbleShown.value && elapsed.value && done.value,
        ready => {
            if (ready) {
                reveal();
            }
        }
    );

    return {run, done, failed, elapsed, revealStep, progress, reveal, start};
};
